import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Phone, Camera, User, CarFront, Image as ImageIcon, ShieldCheck, X } from "lucide-react";

const REASON_LABELS = {
  blocked: "Blocked parking",
  emergency: "Emergency",
  lights: "Lights left on",
  window: "Window open",
  tyre: "Flat tyre",
  scratch: "Scratch / damage",
};

const ID_REQUIRED = new Set(["blocked", "emergency"]);

export default function Report() {
  const { cardCode } = useParams();
  const [params] = useSearchParams();
  const reason = params.get("reason");
  const nav = useNavigate();

  const sessionRaw = sessionStorage.getItem("qaab_scan_session");
  const session = sessionRaw ? JSON.parse(sessionRaw) : null;

  // step machine
  const needsIdentity = ID_REQUIRED.has(reason);
  const steps = needsIdentity
    ? ["intro", "name", "phone", "otp", "plate", "selfie", "car", "scene", "review"]
    : ["intro", "note", "scene_optional", "review"];

  const [stepIdx, setStepIdx] = useState(0);
  const step = steps[stepIdx];
  const next = () => setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  const prev = () => setStepIdx((i) => Math.max(i - 1, 0));

  // identity fields
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [plate, setPlate] = useState("");
  const [selfieUrl, setSelfieUrl] = useState("");
  const [carPhotoUrl, setCarPhotoUrl] = useState("");
  const [sceneUrl, setSceneUrl] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!session) {
      toast.error("Scan session expired. Re-scan the card.");
      nav(`/s/${cardCode}`);
    }
  }, []);

  const requestOtp = async () => {
    try {
      const { data } = await api.post("/scan/otp/request", { phone, session_token: session.session_token });
      setDevOtp(data.dev_otp);
      toast.success("OTP sent");
      next();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const verifyOtp = async () => {
    try {
      await api.post("/scan/otp/verify", { phone, otp, session_token: session.session_token });
      setPhoneVerified(true);
      toast.success("Phone verified");
      next();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        session_token: session.session_token,
        reason,
        note: note || undefined,
        scene_photo_url: sceneUrl || undefined,
        scene_gps_lat: session.gps?.gps_lat,
        scene_gps_lng: session.gps?.gps_lng,
      };
      if (needsIdentity) {
        body.identity = {
          name,
          phone,
          phone_verified: true,
          plate_number: plate,
          selfie_url: selfieUrl || undefined,
          car_photo_url: carPhotoUrl || undefined,
        };
      }
      const { data } = await api.post("/alerts", body);
      toast.success("Alert dispatched");
      sessionStorage.removeItem("qaab_scan_session");
      nav(`/s/${cardCode}/success`, { state: { reference: data.alert_reference } });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = Math.round((stepIdx / (steps.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <header className="bg-[#111827] text-white py-4 sticky top-0 z-10">
        <div className="max-w-md mx-auto px-6 flex items-center justify-between">
          <button onClick={() => stepIdx === 0 ? nav(`/s/${cardCode}`) : prev()} className="flex items-center gap-2 text-sm" data-testid="report-back">
            <ArrowLeft size={16} /> {stepIdx === 0 ? "Cancel" : "Back"}
          </button>
          <span className="font-mono text-[10px] text-gray-400">{REASON_LABELS[reason]}</span>
        </div>
        <div className="h-[2px] bg-white/10 mt-3">
          <div className="h-full bg-[#002FA7] transition-all duration-300" style={{ width: `${progressPct}%` }} data-testid="progress-bar" />
        </div>
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-6 py-8">
        {step === "intro" && (
          <Card>
            <Eyebrow>{REASON_LABELS[reason]}</Eyebrow>
            <H>Let's send a quick alert.</H>
            {needsIdentity ? (
              <P>This is a serious report. We'll need your name, phone (OTP verified), plate, a quick selfie + car photo + scene photo. Takes ~90 seconds.</P>
            ) : (
              <P>Anonymous is fine for this one. Add a quick note or photo if you like — then send.</P>
            )}
            <Btn onClick={next} testId="report-start">Continue <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "name" && (
          <Card>
            <Eyebrow>Step 1 of 7</Eyebrow>
            <H>Your name</H>
            <P>So the driver knows who reached out.</P>
            <Input testId="report-name-input" value={name} onChange={setName} placeholder="Your full name" />
            <Btn onClick={next} disabled={name.length < 2} testId="report-name-next">Continue <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "phone" && (
          <Card>
            <Eyebrow>Step 2 of 7</Eyebrow>
            <H>Your phone</H>
            <P>We'll send a 6-digit code to verify. Your number stays private.</P>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="report-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input-flat pl-10"
                placeholder="+973 3xxx xxxx"
                type="tel"
              />
            </div>
            <Btn onClick={requestOtp} disabled={phone.length < 6} testId="report-phone-next">Send code <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "otp" && (
          <Card>
            <Eyebrow>Step 3 of 7</Eyebrow>
            <H>Enter the code</H>
            <P>Sent to <span className="font-mono">{phone}</span></P>
            {devOtp && (
              <div className="border border-yellow-300 bg-yellow-50 p-3 font-mono text-sm mb-4" data-testid="report-dev-otp">
                DEV — OTP: <span className="font-bold">{devOtp}</span>
              </div>
            )}
            <input
              data-testid="report-otp-input"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="input-flat text-center text-2xl font-mono tracking-[0.5em]"
              inputMode="numeric"
              placeholder="------"
            />
            <Btn onClick={verifyOtp} disabled={otp.length !== 6} testId="report-otp-verify">Verify <ShieldCheck size={16} /></Btn>
          </Card>
        )}

        {step === "plate" && (
          <Card>
            <Eyebrow>Step 4 of 7</Eyebrow>
            <H>Your plate number</H>
            <P>Your own car's plate — for accountability.</P>
            <Input
              testId="report-plate-input"
              value={plate}
              onChange={(v) => setPlate(v.toUpperCase())}
              placeholder="5678 BHR"
              mono
            />
            <Btn onClick={next} disabled={plate.length < 3} testId="report-plate-next">Continue <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "selfie" && (
          <Card>
            <Eyebrow>Step 5 of 7</Eyebrow>
            <H>Quick selfie</H>
            <P>Live capture only — verifies you're a real person.</P>
            <PhotoCapture capture="user" value={selfieUrl} onChange={setSelfieUrl} icon={<User size={20} />} testId="selfie-capture" />
            <Btn onClick={next} disabled={!selfieUrl} testId="selfie-next">Continue <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "car" && (
          <Card>
            <Eyebrow>Step 6 of 7</Eyebrow>
            <H>Photo of your car</H>
            <P>Show your car (with its plate visible if possible).</P>
            <PhotoCapture capture="environment" value={carPhotoUrl} onChange={setCarPhotoUrl} icon={<CarFront size={20} />} testId="car-capture" />
            <Btn onClick={next} disabled={!carPhotoUrl} testId="car-next">Continue <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "scene" && (
          <Card>
            <Eyebrow>Step 7 of 7</Eyebrow>
            <H>Photo of the scene</H>
            <P>Show the actual incident — what's blocking, the emergency, etc.</P>
            <PhotoCapture capture="environment" value={sceneUrl} onChange={setSceneUrl} icon={<ImageIcon size={20} />} testId="scene-capture" />
            <Btn onClick={next} disabled={!sceneUrl} testId="scene-next">Continue <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "note" && (
          <Card>
            <Eyebrow>Optional note</Eyebrow>
            <H>Anything to add?</H>
            <P>Short note for the driver (optional).</P>
            <textarea
              data-testid="report-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 240))}
              className="input-flat min-h-[100px]"
              placeholder="Headlights are on, second row from entrance…"
            />
            <Btn onClick={next} testId="report-note-next">Continue <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "scene_optional" && (
          <Card>
            <Eyebrow>Optional photo</Eyebrow>
            <H>Add a scene photo?</H>
            <P>Helpful but not required for this report.</P>
            <PhotoCapture capture="environment" value={sceneUrl} onChange={setSceneUrl} icon={<ImageIcon size={20} />} testId="optional-scene" />
            <Btn onClick={next} testId="optional-scene-next">Continue <ArrowRight size={16} /></Btn>
          </Card>
        )}

        {step === "review" && (
          <Card>
            <Eyebrow>Review & send</Eyebrow>
            <H>Looks good?</H>
            <div className="space-y-2 text-sm mb-6">
              <Row label="Reason" value={REASON_LABELS[reason]} />
              {needsIdentity && (
                <>
                  <Row label="Name" value={name} />
                  <Row label="Phone" value={phone} mono />
                  <Row label="Plate" value={plate} mono />
                </>
              )}
              {note && <Row label="Note" value={note} />}
            </div>
            <Btn onClick={submit} disabled={submitting} testId="report-submit">
              {submitting ? "Sending…" : "Send alert"} <ShieldCheck size={16} />
            </Btn>
          </Card>
        )}
      </main>
    </div>
  );
}

function Card({ children }) { return <div className="card-flat p-6 space-y-4">{children}</div>; }
function Eyebrow({ children }) { return <div className="label-eyebrow">{children}</div>; }
function H({ children }) { return <h1 className="font-display font-black text-3xl tracking-tight">{children}</h1>; }
function P({ children }) { return <p className="text-gray-600 leading-relaxed">{children}</p>; }
function Input({ value, onChange, placeholder, testId, mono }) {
  return (
    <input
      data-testid={testId}
      className={`input-flat ${mono ? "font-mono tracking-wider uppercase text-lg" : ""}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}
function Btn({ onClick, children, disabled, testId }) {
  return (
    <button onClick={onClick} disabled={disabled} className="btn btn-primary w-full" data-testid={testId}>
      {children}
    </button>
  );
}
function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
      <span className="label-eyebrow">{label}</span>
      <span className={mono ? "font-mono" : "font-medium text-right"}>{value}</span>
    </div>
  );
}

function PhotoCapture({ capture, value, onChange, icon, testId }) {
  const ref = useRef(null);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Photo too large (max 4MB)"); return; }
    const r = new FileReader();
    r.onloadend = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  if (value) {
    return (
      <div className="relative" data-testid={`${testId}-preview`}>
        <img src={value} alt="captured" className="w-full max-h-[300px] object-cover border border-gray-200" />
        <button
          onClick={() => onChange("")}
          className="absolute top-2 right-2 w-8 h-8 bg-white/90 border border-gray-200 grid place-items-center"
          data-testid={`${testId}-clear`}
        >
          <X size={14} />
        </button>
      </div>
    );
  }
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture={capture}
        className="hidden"
        onChange={onFile}
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="w-full border-2 border-dashed border-gray-300 p-8 grid place-items-center text-gray-500 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
        data-testid={testId}
      >
        <div className="w-12 h-12 bg-[#F9F9F8] border border-gray-200 grid place-items-center mb-3">
          <Camera size={20} />
        </div>
        <div className="font-display font-bold text-base">Open camera</div>
        <div className="text-xs text-gray-500 mt-1">Live capture · no gallery</div>
      </button>
    </>
  );
}
